import React, { Fragment, type ReactNode } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import classNames from 'classnames';
import { MacScrollbar } from 'mac-scrollbar';
import { chainflipAssetMap, FLIP_SYMBOL, type TokenAmount } from '@/shared/utils';
import SuccessHero from '../../assets/svg/success-illustration.svg';
import { CloseIcon } from '../../icons/large';
import { SkeletonLine } from '../atoms/LoadingSkeleton';
import { TokenWithChainByAsset } from '../logo/TokenWithChain';
import QuestionMarkTooltip from '../QuestionMarkTooltip';
import { UsdAmount } from '../UsdAmount';

interface ModalProps {
  open: boolean;
  children: ReactNode;
  onClose: () => void;
  initialFocus?: React.RefObject<HTMLElement>;
  beforeEnter?: () => void;
  afterLeave?: () => void;
}

type ModalContentProps = {
  children: ReactNode;
  buttons?: ReactNode;
  className?: string;
  success?: boolean;
  title: string;
  titleSize?: 'normal' | 'large';
  description?: ReactNode;
  step?: { current: number; max: number };
  buttonsClassName?: string;
  scrollableContent?: boolean;
};

export const ModalContent = ({
  children,
  buttons,
  className,
  success,
  description,
  title,
  titleSize = 'normal',
  step,
  buttonsClassName,
  scrollableContent,
}: ModalContentProps) => (
  <div className={classNames('flex flex-col gap-6', className)}>
    {success ? (
      <>
        <div className="pointer-events-none absolute inset-0 flex justify-center">
          <SuccessHero />
        </div>
        <div className="mt-[80px] flex w-full flex-col items-center">
          <div className="font-aeonikMedium text-24 text-cf-white">{title}</div>
          {description}
        </div>
      </>
    ) : (
      <>
        <div className="space-y-1">
          {step && (
            <span className="text-12 text-cf-green-1">
              Step {step.current}/{step.max}
            </span>
          )}
          <div
            className={classNames('flex font-aeonikMedium text-cf-white', {
              'text-24': titleSize === 'large',
              'text-16': titleSize === 'normal',
            })}
          >
            {title}
          </div>
          {description && (
            <div className="text-14 font-normal leading-[18px] text-cf-light-2">{description}</div>
          )}
        </div>
        <div className="h-px w-full bg-cf-gray-4" />
      </>
    )}
    <div>
      {scrollableContent ? (
        <MacScrollbar className="max-h-[400px] overflow-y-auto" skin="dark">
          {children}
        </MacScrollbar>
      ) : (
        children
      )}
    </div>
    {buttons && (
      <div className={classNames('flex justify-end space-x-4', buttonsClassName)}>{buttons}</div>
    )}
  </div>
);

export const DashboardModal = ({
  open,
  children,
  onClose,
  initialFocus,
  beforeEnter,
  afterLeave,
}: ModalProps): JSX.Element => (
  <Transition appear show={open} as={Fragment} beforeEnter={beforeEnter} afterLeave={afterLeave}>
    <Dialog
      onClose={onClose}
      as="div"
      className="fixed inset-0 z-50 overflow-y-auto"
      initialFocus={initialFocus}
    >
      <Transition.Child
        as={Fragment}
        enter="ease-out duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="ease-in duration-200"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div
          className="fixed inset-0 bg-[rgba(0,0,0,0.55)] backdrop-blur-[6px]"
          onClick={(e) => {
            // prevent triggering click event on parent component
            e.stopPropagation();
          }}
        />
      </Transition.Child>

      <div className="flex h-full w-full flex-col items-center justify-center">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <Dialog.Panel as="div">
            <Dialog.Title />
            <Dialog.Description />
            {children}
          </Dialog.Panel>
        </Transition.Child>
      </div>
    </Dialog>
  </Transition>
);

export const DashboardModalWithCloseButton = ({
  onClose,
  open,
  afterLeave,
  beforeEnter,
  initialFocus,
  children,
}: ModalProps): JSX.Element => (
  <DashboardModal
    onClose={onClose}
    open={open}
    afterLeave={afterLeave}
    beforeEnter={beforeEnter}
    initialFocus={initialFocus}
  >
    <div className="flex max-w-[500px] flex-col rounded-md border border-cf-gray-4 bg-cf-gray-3 p-6 max-md:mx-1 md:min-w-[487px]">
      <button
        type="button"
        className="absolute z-10 self-end transition hover:text-cf-white"
        onClick={onClose}
      >
        <CloseIcon />
      </button>
      {children}
    </div>
  </DashboardModal>
);

export const StatsPanel = ({
  items,
  loading,
}: {
  items: (
    | {
        Icon?: React.FC;
        label: string;
        content: ReactNode;
        loading?: boolean;
        tooltipContent?: string;
      }
    | {
        Icon?: React.FC;
        label: string;
        flipAmount: TokenAmount | null | undefined;
        loading?: boolean;
        tooltipContent?: string;
        showPrice?: boolean;
      }
  )[];
  loading?: boolean;
}) => (
  <div className="flex w-full flex-col gap-y-3 rounded-md border border-cf-gray-4 bg-cf-gray-3-5 p-4 font-aeonikMedium text-13 text-cf-light-2 shadow-grayPop3">
    {items.map(({ Icon = () => null, label, loading: itemLoading, tooltipContent, ...rest }) => {
      const content =
        'content' in rest
          ? rest.content
          : rest.flipAmount && (
              <>
                {rest.flipAmount.toFormatted()} <TokenWithChainByAsset asset="Flip" /> {FLIP_SYMBOL}{' '}
                {rest.showPrice && (
                  <span className="text-cf-light-2">
                    <UsdAmount
                      token={chainflipAssetMap.Flip}
                      tokenAmount={rest.flipAmount}
                      withParens
                    />
                  </span>
                )}
              </>
            );

      return (
        <div key={label} className="flex items-center justify-between gap-x-4">
          <div className="flex items-center gap-x-2">
            <Icon /> {label} {tooltipContent && <QuestionMarkTooltip content={tooltipContent} />}
          </div>
          <div className="flex items-center gap-x-1 justify-self-end text-cf-light-3">
            {loading || itemLoading ? <SkeletonLine width={100} /> : content}
          </div>
        </div>
      );
    })}
  </div>
);
